import { useMemo } from "react";
import { SingleSelectInput, Text, TextArea } from "@ucl/react-library";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBoundInput } from "src/hooks/use-bound-input";
import { ChallengeFormValues } from "src/types/challenge-form-types";
import { ChallengeReasons } from "src/api/types/enums";
import { ChallengeReasonInfo } from "./challenge-reason-info";

const VISIBLE_REASONS = [
  ChallengeReasons.AgreementDetailsWrong,
  ChallengeReasons.CommissionArrangementOrInterestRateAssessment,
  ChallengeReasons.AnotherReason,
];

export const ChallengeReason = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<ChallengeFormValues>();

  const challengeReasonProps = useBoundInput({ name: "challengeReason", control });
  const challengeExplanationProps = useBoundInput({ name: "challengeExplanation", control });

  const reasonOptions = useMemo(
    () => VISIBLE_REASONS.map((value) => ({ label: t(`challengeReason.options.${value}`), value })),
    [t]
  );

  const selectedReason = VISIBLE_REASONS.find((reason) => reason === challengeReasonProps.value);

  return (
    <div data-testid="challengeReason" className="flex flex-col gap-5 w-full">
      <div className="flex flex-col gap-3">
        <Text.P size="large">{t("challengeReason.intro.choose")}</Text.P>
        <Text.P size="large">{t("challengeReason.intro.explainLater")}</Text.P>
      </div>

      <SingleSelectInput
        data-testid="challengeReasonSelect"
        labelProp={{ label: t("challengeReason.label") }}
        options={reasonOptions}
        selected={challengeReasonProps.value}
        onOptionSelect={(option) => challengeReasonProps.onChange(option.value)}
        inputState={challengeReasonProps.inputState}
        helperTextProp={challengeReasonProps.helperTextProp}
        className="w-full"
        isSearchable={false}
      />

      <ChallengeReasonInfo reason={selectedReason} />

      <Text renderAs="h2" styleType="h4" fontWeight="semi-bold">
        {t("challengeReason.explainYourChallenge")}
      </Text>

      <Text.P size="large">
        {t("challengeReason.explainYourChallengeText")}
      </Text.P>

      <TextArea
        data-testid="challengeReasonExplanation"
        {...challengeExplanationProps}
        helperTextProp={{
          ...challengeExplanationProps.helperTextProp,
          defaultProp: { helperText: t("challengeReason.explainYourChallengeHelperText") },
        }}
        maxCharacters={1000}
        textSize="large"
      />
    </div>
  );
};
